import { api } from './api';
import type {
  ApiResponse, PaginatedResponse,
  AuthResponse, User,
  Trip, TripSearchParams,
  Parcel,
  Booking, Message, Review, Notification,
} from '../types';

// ─── Auth ────────────────────────────────────────────────────

export const authService = {
  register: (data: { email: string; password: string; firstName: string; lastName: string; phone?: string }) =>
    api.post<ApiResponse<AuthResponse>>('/auth/register', data).then((r) => r.data),

  login: (data: { email: string; password: string }) =>
    api.post<ApiResponse<AuthResponse>>('/auth/login', data).then((r) => r.data),

  logout: () =>
    api.post('/auth/logout').then((r) => r.data),

  refreshToken: (refreshToken: string) =>
    api.post<ApiResponse<AuthResponse['tokens']>>('/auth/refresh', { refreshToken }).then((r) => r.data),

  getMe: () =>
    api.get<ApiResponse<User>>('/users/me').then((r) => r.data),
};

// ─── Trips ──────────────────────────────────────────────────

export const tripsService = {
  search: (params: TripSearchParams) =>
    api.get<PaginatedResponse<Trip>>('/trips', { params }).then((r) => r.data),

  getById: (id: string) =>
    api.get<ApiResponse<Trip>>(`/trips/${id}`).then((r) => r.data),

  create: (data: Omit<Trip, 'id' | 'transporterId' | 'transporter' | 'status' | 'createdAt'>) =>
    api.post<ApiResponse<Trip>>('/trips', data).then((r) => r.data),

  update: (id: string, data: Partial<Trip>) =>
    api.patch<ApiResponse<Trip>>(`/trips/${id}`, data).then((r) => r.data),

  cancel: (id: string) =>
    api.delete<ApiResponse<Trip>>(`/trips/${id}`).then((r) => r.data),

  getMine: () =>
    api.get<PaginatedResponse<Trip>>('/trips/mine').then((r) => r.data),
};

// ─── Parcels ─────────────────────────────────────────────────

export const parcelsService = {
  create: (data: { tripId: string; description: string; weightKg: number; photo?: File }) => {
    const form = new FormData();
    Object.entries(data).forEach(([k, v]) => { if (v !== undefined) form.append(k, v as string | Blob); });
    return api.post<ApiResponse<Parcel>>('/parcels', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },

  getMine: () =>
    api.get<PaginatedResponse<Parcel>>('/parcels/mine').then((r) => r.data),

  getById: (id: string) =>
    api.get<ApiResponse<Parcel>>(`/parcels/${id}`).then((r) => r.data),
};

// ─── Bookings ────────────────────────────────────────────────

export const bookingsService = {
  create: (data: { parcelId: string; tripId: string }) =>
    api.post<ApiResponse<{ booking: Booking; clientSecret: string }>>('/bookings', data).then((r) => r.data),

  getAll: () =>
    api.get<PaginatedResponse<Booking>>('/bookings').then((r) => r.data),

  getById: (id: string) =>
    api.get<ApiResponse<Booking>>(`/bookings/${id}`).then((r) => r.data),

  updateStatus: (id: string, deliveryStatus: Booking['deliveryStatus']) =>
    api.patch<ApiResponse<Booking>>(`/bookings/${id}/status`, { deliveryStatus }).then((r) => r.data),

  confirmDelivery: (id: string, confirmationCode: string) =>
    api.post<ApiResponse<Booking>>(`/bookings/${id}/confirm`, { confirmationCode }).then((r) => r.data),
};

// ─── Messages ────────────────────────────────────────────────

export const messagesService = {
  getByBooking: (bookingId: string) =>
    api.get<ApiResponse<Message[]>>(`/bookings/${bookingId}/messages`).then((r) => r.data),

  send: (bookingId: string, content: string) =>
    api.post<ApiResponse<Message>>(`/bookings/${bookingId}/messages`, { content }).then((r) => r.data),
};

// ─── Reviews ─────────────────────────────────────────────────

export const reviewsService = {
  create: (bookingId: string, data: { rating: number; comment?: string }) =>
    api.post<ApiResponse<Review>>(`/bookings/${bookingId}/reviews`, data).then((r) => r.data),

  getByUser: (userId: string) =>
    api.get<PaginatedResponse<Review>>(`/users/${userId}/reviews`).then((r) => r.data),
};

// ─── Notifications ───────────────────────────────────────────

export const notificationsService = {
  getAll: () =>
    api.get<PaginatedResponse<Notification>>('/notifications').then((r) => r.data),

  markAllRead: () =>
    api.patch('/notifications/read').then((r) => r.data),
};

// ─── Users ───────────────────────────────────────────────────

export const usersService = {
  getMe: () =>
    api.get<ApiResponse<User>>('/users/me').then((r) => r.data),

  updateMe: (data: Partial<Pick<User, 'firstName' | 'lastName' | 'phone'>>) =>
    api.patch<ApiResponse<User>>('/users/me', data).then((r) => r.data),

  uploadKyc: (file: File, docType: 'CNI' | 'PASSPORT' | 'RESIDENCE_PERMIT') => {
    const form = new FormData();
    form.append('document', file);
    form.append('docType', docType);
    return api.post<ApiResponse<{ status: string }>>('/users/me/kyc', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },

  getProfile: (userId: string) =>
    api.get<ApiResponse<User>>(`/users/${userId}/profile`).then((r) => r.data),
};
